import { TOOL_MATERIALS, ToolMaterialId } from 'constants/tool-materials'
import { TOOL_ROLES, ToolRole } from 'constants/tool-roles'
import { TOOL_TYPES, ToolTypeId } from 'constants/tool-types'
import {
  FieldSpec,
  InstanceProperties,
  RichType,
  getEnumValuesFromMap,
} from 'lib/rich'
import { UnitShorthand } from 'types/unit-shorthand'
import { unit_spec } from './common'

// Allow specifying "preToolId" which will be useful for conversion routines
type FieldSpecWithPretoolId = FieldSpec & { preToolId: string }

// Define tool type shorthands for more concise definitiions
const BALL = ToolTypeId.BallEndmill
const BBAR = ToolTypeId.BoringBar
const CHAMFER = ToolTypeId.ChamferMill
const CBORE = ToolTypeId.CounterBore
const TAP = ToolTypeId.CuttingTap
const DOVETAIL = ToolTypeId.DovetailCutter
const DRILL = ToolTypeId.Drill
const FACE = ToolTypeId.FaceMill
const FLAT = ToolTypeId.FlatEndmill
const FLYCUT = ToolTypeId.Flycutter
const FORMTAP = ToolTypeId.FormTap
const KEYSEAT = ToolTypeId.KeyseatCutter
const LOLLIPOP = ToolTypeId.LollipopEndmill
const RADIUS = ToolTypeId.RadiusEndmill
const REAMER = ToolTypeId.Reamer
const TMILL = ToolTypeId.ThreadMill
const CSINK = ToolTypeId.CounterSink
const TAPS = [TAP, FORMTAP]
const RS = [
  BALL,
  FACE,
  CSINK,
  FLAT,
  DOVETAIL,
  FLYCUT,
  KEYSEAT,
  LOLLIPOP,
  RADIUS,
]

const typeEnumValues = TOOL_TYPES.map(({ id, name }) => ({
  value: id,
  title: name,
}))

const materialEnumValues = TOOL_MATERIALS.map(({ id, name }) => ({
  value: id,
  title: name,
}))
const roleEnumValues = getEnumValuesFromMap(TOOL_ROLES)

function typeIn(...types: ToolTypeId[]): { typeId: { in: ToolTypeId[] } } {
  return { typeId: { in: types } }
}
function typeNotIn(...types: ToolTypeId[]): {
  typeId: { notIn: ToolTypeId[] }
} {
  return { typeId: { notIn: types } }
}

export class TpTool extends RichType {
  static typeId_spec: FieldSpec = {
    title: 'Type',
    kind: 'enum',
    required: true,
    //hidden: true,
    enumValues: typeEnumValues,
    defaultValue: typeEnumValues[0].value,
  }
  typeId?: ToolTypeId

  static unit_spec: FieldSpec = { ...unit_spec, hidden: false }
  unit: UnitShorthand

  static toolMaterialId_spec: FieldSpecWithPretoolId = {
    title: 'Material',
    kind: 'enum',
    required: false,
    preToolId: 'material',
    enumValues: materialEnumValues,
    defaultValue: 1,
  }
  toolMaterialId?: ToolMaterialId

  static role_spec: FieldSpec = {
    title: 'Role',
    kind: 'enum',
    required: false,
    enumValues: roleEnumValues,
    defaultValue: ToolRole.GENERAL_PURPOSE,
    enabledWhen: typeIn(BALL, FACE, FLAT, RADIUS, DRILL),
  }
  role?: ToolRole

  ////////////////////////////////////////////////////////////
  // Geometry
  static geometry_spec = { title: 'Geometry', kind: 'group' }

  static diameter_spec: FieldSpecWithPretoolId = {
    title: 'Diameter',
    kind: 'length',
    preToolId: 'geometry_cuttingDiameter',
    defaultValueMM: 6,
    defaultValueIN: 0.25,
    enabledWhen: typeNotIn(...TAPS),
    required: true,
    gt: 0.0,
  }
  diameter?: number

  static cornerRadius_spec: FieldSpecWithPretoolId = {
    title: 'Corner Radius',
    kind: 'length',
    preToolId: 'geometry_cornerRadius',
    defaultValueMM: 1.5,
    defaultValueIN: 0.125,
    enabledWhen: typeIn(FACE, FLYCUT, KEYSEAT, RADIUS),
    required: true,
    gte: 0.0,
  }
  cornerRadius?: number

  static bottomDiameter_spec: FieldSpec = {
    title: 'Bottom Diameter', // "Radial DOC" for keyseatCutter
    kind: 'length',
    defaultValueIN: 0.0,
    enabledWhen: typeIn(CHAMFER, CSINK, KEYSEAT),
    required: true,
    gte: 0.0,
  }
  bottomDiameter?: number | null // tip diameter except for the slot mill case, where it's calculated as fusionTool.geometry.DC - getShankDiameter(fusionTool)) / 2

  static stickout_spec: FieldSpecWithPretoolId = {
    title: 'Length Below Holder',
    kind: 'length',
    preToolId: 'geometry_overallLength',
    defaultValueMM: 35,
    defaultValueIN: 1.5,
    required: true,
    gt: 0.0,
  }
  stickout?: number

  static fluteLength_spec: FieldSpecWithPretoolId = {
    title: 'Flute Length', // "Cutter Width" for keyseatCutter
    kind: 'length',
    preToolId: 'geometry_fluteLength',
    defaultValueMM: 20,
    defaultValueIN: 0.75,
    enabledWhen: typeNotIn(FORMTAP, TAP, LOLLIPOP),
    required: true,
    gt: 0.0,
  }
  fluteLength?: number

  // NOTE: mutually exclusive with fluteLength, with threadLength only used on threads (presumably)
  static threadLength_spec: FieldSpecWithPretoolId = {
    title: 'Thread Length',
    kind: 'length',
    preToolId: 'geometry_fluteLength',
    defaultValueMM: 20,
    defaultValueIN: 0.75,
    enabledWhen: typeIn(...TAPS),
    required: true,
    gt: 0.0,
  }
  threadLength?: number

  static fluteCount_spec: FieldSpecWithPretoolId = {
    title: 'Flute Count',
    kind: 'integer',
    preToolId: 'geometry_numberOfFlutes',
    defaultValue: 3,
    enabledWhen: typeNotIn(...TAPS),
    required: true,
    gt: 0.0,
  }
  fluteCount?: number

  static tipAngle_spec: FieldSpecWithPretoolId = {
    title: 'Tip Angle', // "½ Tip Angle" for chamferMill, "Globe Angle" for lollipopEndmill, "Included Angle" for dovetailCutter
    kind: 'angle',
    preToolId: 'geometry_tipAngle',
    defaultValue: 0.0,
    enabledWhen: typeIn(CHAMFER, CSINK, DOVETAIL, DRILL, LOLLIPOP),
    required: true,
    gte: 0.0,
  }
  tipAngle?: number

  static shoulderDiameter_spec: FieldSpecWithPretoolId = {
    title: 'Shoulder Diameter',
    kind: 'length',
    preToolId: 'geometry_shaftDiameter',
    defaultValueMM: 6,
    defaultValueIN: 0.25,
    enabledWhen: typeIn(BALL, FLAT, DOVETAIL, KEYSEAT, LOLLIPOP, RADIUS),
    required: true,
    gt: 0.0,
  }
  shoulderDiameter?: number

  static threadPitch_spec: FieldSpecWithPretoolId = {
    title: 'Thread Pitch',
    kind: 'length',
    preToolId: 'geometry_threadPitch',
    defaultValueMM: 1,
    defaultValueIN: 0.05,
    enabledWhen: typeIn(...TAPS, TMILL),
    required: true,
    gt: 0.0,
  }
  threadPitch?: number

  // TODO: We can switch to using diameter field for this. Everywhere they are the same. We just need to update engine ToolpathPackages/ToolsTP/src/json.jl to *not* map nominalDiameter to diameter
  static nominalDiameter_spec: FieldSpec = {
    title: 'Nominal Diameter',
    kind: 'length',
    defaultValueMM: 6,
    defaultValueIN: 0.25,
    enabledWhen: typeIn(...TAPS),
    required: true,
    gt: 0.0,
  }
  nominalDiameter?: number

  static threadProfileAngle_spec: FieldSpecWithPretoolId = {
    title: 'Thread Profile Angle',
    kind: 'angle',
    preToolId: 'geometry_threadProfileAngle',
    defaultValue: 60.0,
    enabledWhen: typeIn(TMILL),
    required: true,
    gte: 0.0,
  }
  threadProfileAngle?: number

  static numberOfTeeth_spec: FieldSpecWithPretoolId = {
    title: 'Number of Teeth',
    kind: 'integer',
    preToolId: 'geometry_numberOfTeeth',
    defaultValue: 1,
    enabledWhen: typeIn(TMILL),
    required: true,
    gte: 0,
  }
  numberOfTeeth?: number

  static leadAngle_spec: FieldSpec = {
    title: 'Lead Angle',
    kind: 'angle',
    defaultValue: 1.0,
    enabledWhen: typeIn(FACE, FLYCUT),
    required: true,
    gte: 0,
  }
  leadAngle?: number | null

  static partialThreadCount_spec: FieldSpec = {
    title: 'Partial Thread Count',
    kind: 'integer',
    hidden: true,
    defaultValue: 0,
    enabledWhen: typeIn(...TAPS),
    required: true,
    gte: 0,
  }
  partialThreadCount?: number | null // currently unused, not imported

  static relieved_spec = {
    title: 'Relieved Shank',
    kind: 'group',
  }

  static neckLength_spec: FieldSpec = {
    title: 'Neck Length',
    kind: 'length',
    //readOnly: true,
    defaultValue: 0.0,
    enabledWhen: typeIn(...RS),
    required: true,
    gte: 0.0,
  }
  neckLength?: number

  static shankDiameter_spec: FieldSpec = {
    title: 'Shank Diameter',
    kind: 'length',
    defaultValueMM: 5.5,
    defaultValueIN: 0.23, // slightly less than diameter
    enabledWhen: typeIn(...RS),
    required: true,
    gte: 0,
  }
  shankDiameter?: number

  static misc_spec = { title: 'Misc', kind: 'group' }

  static maximumRpm_spec: FieldSpec = {
    title: 'Maximum RPM',
    kind: 'rotation_speed',
    hidden: true,
    defaultValue: 2500,
    enabledWhen: typeIn(FLYCUT),
    required: true,
    gt: 0,
  }
  maximumRpm?: number // not currently used anywhere. Not imported in fusion-tools-to-toolpath-tools.ts

  static clockwiseRotation_spec: FieldSpecWithPretoolId = {
    title: 'Clockwise Rotation',
    kind: 'boolean',
    preToolId: 'geometry_clockwise',
    defaultValue: true,
    enabledWhen: typeIn(...TAPS),
    required: true,
  }
  clockwiseRotation?: boolean

  static sideMill_spec: FieldSpec = {
    title: 'Allow Side Milling',
    kind: 'boolean',
    defaultValue: true,
    enabledWhen: typeIn(BALL, FACE, FLAT, RADIUS),
    required: true,
  }
  sideMill: boolean = true

  static toolHolderBottomDiameter_spec: FieldSpec = {
    title: 'Tool Holder Diameter',
    kind: 'length',
    defaultValueIN: 1.0,
    required: false,
  }
  toolHolderBottomDiameter?: number // hardcoded to unit === Unit.INCHES ? 1 : 25.4,

  ////////////////////////////////////////////////////////////
  // Information
  static information_spec = { title: 'Information', kind: 'group' }

  static vendor_spec: FieldSpecWithPretoolId = {
    title: 'Vendor',
    kind: 'text',
    required: false,
    preToolId: 'vendor',
  }
  vendor: string

  static productId_spec: FieldSpecWithPretoolId = {
    title: 'Product ID',
    kind: 'text',
    required: false,
    preToolId: 'productId',
  }
  productId: string

  static link_spec: FieldSpecWithPretoolId = {
    title: 'Link',
    kind: 'url',
    required: false,
    preToolId: 'productLink',
  }
  link: string

  constructor() {
    super()
  }
}

type ExtraToolProps = {
  priority?: 1 | 2 | 3
  data: any
}

export type TpToolData = InstanceProperties<TpTool> & ExtraToolProps
